// src/utils/logoPdf.js
//
// Inserção do logo do MAGO em PDFs gerados com jsPDF.
//
// Por que este módulo existe: o arquivo do logo tem 5349x1913 px e o jsPDF
// embute bitmaps DESCOMPRIMIDOS. Passar a imagem original para addImage custa
// ~41 MB de PDF (5349 x 1913 x 4 bytes) para algo que ocupa 3 cm no papel — e,
// quando a chamada está dentro de um laço por página, esse custo se repete a
// cada página. Aqui o logo é redesenhado num canvas no tamanho real de
// impressão e recebe um alias, para que o jsPDF referencie um único bitmap em
// todas as páginas.

import logo from '../assets/logo-mago-2.png';

const DPI_IMPRESSAO = 300;
const MM_POR_POLEGADA = 25.4;

/**
 * Carrega e rasteriza o logo no tamanho em que ele será impresso.
 *
 * Nunca lança: se a imagem ou o canvas falharem, devolve null e o relatório
 * sai sem logo — perder o cabeçalho é melhor do que perder a exportação.
 * @param {number} larguraMm Largura do logo no papel, em milímetros.
 * @param {number} alturaMm Altura do logo no papel, em milímetros.
 * @return {Promise<object|null>} { dataUrl, alias, larguraMm, alturaMm } ou null.
 */
export const prepararLogoPdf = async (larguraMm, alturaMm) => {
  try {
    const img = new Image();
    img.src = logo;
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
    if (!img.naturalWidth) return null;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round((larguraMm / MM_POR_POLEGADA) * DPI_IMPRESSAO);
    canvas.height = Math.round((alturaMm / MM_POR_POLEGADA) * DPI_IMPRESSAO);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return {
      dataUrl: canvas.toDataURL('image/png'),
      // O alias precisa distinguir tamanhos: cada dimensão gera um bitmap
      // diferente, e reaproveitar o alias faria o jsPDF desenhar o bitmap
      // errado no segundo tamanho.
      alias: `logoMago_${larguraMm}x${alturaMm}`,
      larguraMm,
      alturaMm,
    };
  } catch {
    return null;
  }
};

/**
 * Desenha o logo preparado. Silencioso quando não há logo disponível.
 * @param {object} doc Documento jsPDF.
 * @param {object|null} logoPreparado Retorno de prepararLogoPdf.
 * @param {number} x Posição horizontal, em milímetros.
 * @param {number} y Posição vertical, em milímetros.
 */
export const desenharLogoPdf = (doc, logoPreparado, x, y) => {
  if (!logoPreparado) return;
  try {
    doc.addImage(
      logoPreparado.dataUrl,
      'PNG',
      x,
      y,
      logoPreparado.larguraMm,
      logoPreparado.alturaMm,
      logoPreparado.alias,
      'FAST',
    );
  } catch {
    // Logo indisponível: o relatório sai sem ele.
  }
};
